// src/utils/superPrompt.js

// src/utils/superPrompt.js

const translateToEnglish = async (text) => {
  if (!text || !text.trim()) return "";
  
  // Wenn Text bereits Englisch ist, direkt zurückgeben
  const hasGermanChars = /[äöüßÄÖÜ]/.test(text);
  const hasCommonGermanWords = /(der|die|das|und|auf|mit|am|im|von|zu|in)\s/i.test(text);
  
  if (!hasGermanChars && !hasCommonGermanWords) {
    return text; // Vermutlich schon Englisch
  }
  
  try {
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=en&dt=t&q=${encodeURIComponent(text)}`;
    const res = await fetch(url);
    
    if (!res.ok) {
      console.warn('Translation failed, using original text');
      return text;
    }
    
    const data = await res.json();
    const translated = data[0][0][0];
    
    console.log('🌐 Translation:', text, '→', translated);
    
    return translated || text;
  } catch (error) {
    console.error('Translation error:', error);
    return text; // Fallback auf Original
  }
};

// Zufall aus Array
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Kamera / Framing / Licht-Presets für Diversity
const CAMERA_OPTIONS = [
  "shot on 35mm lens",
  "shot on 50mm lens",
  "shot on 85mm lens",
  "shot on 135mm telephoto lens"
];

const ANGLE_OPTIONS = [
  "camera at eye level",
  "slightly low angle shot",
  "slightly high angle shot",
  "dynamic angle"
];

const LIGHTING_OPTIONS = [
  "soft natural daylight",
  "golden hour sunset lighting",
  "soft studio softbox lighting",
  "cinematic moody lighting",
  "clean commercial lighting"
];

// Cinematic Mood Presets - RADIKAL VEREINFACHT (nur Framing!)
const CINEMATIC_MOODS = [
  {
    id: "golden",
    name: "Golden Hour",
    framing: "wide shot, person in scene, shot on 35mm lens"
  },
  {
    id: "blue",
    name: "Blue Hour",
    framing: "wide shot, person in scene, shot on 35mm lens"
  },
  {
    id: "noir",
    name: "Film Noir",
    framing: "wide shot, person in scene, shot on 50mm lens"
  },
  {
    id: "neon",
    name: "Neon Lights",
    framing: "wide shot, person in scene, shot on 35mm lens"
  },
  {
    id: "storm",
    name: "Storm Light",
    framing: "wide shot, person in scene, shot on 24mm wide-angle lens"
  },
  {
    id: "night",
    name: "Night City",
    framing: "wide shot, person in scene, shot on 50mm lens"
  },
  {
    id: "epic",
    name: "Epic Film",
    framing: "wide establishing shot, person in scene, shot on 24mm wide-angle lens"
  }
];

// Portrait Styles - RADIKAL VEREINFACHT (nur Framing!)
const PORTRAIT_STYLES = {
  natural: {
    name: "Natürlich",
    framing: "portrait photo, head and shoulders visible, shot on 85mm lens"
  },
  beauty: {
    name: "Beauty-Licht",
    framing: "portrait photo, head and shoulders visible, shot on 85mm lens"
  },
  glamour: {
    name: "Glamour",
    framing: "portrait photo, head and shoulders visible, shot on 85mm lens"
  },
  moody: {
    name: "Moody",
    framing: "portrait photo, head and shoulders visible, shot on 85mm lens"
  },
  studio: {
    name: "Studio",
    framing: "portrait photo, head and shoulders visible, shot on 85mm lens"
  },
  vintage: {
    name: "Vintage",
    framing: "portrait photo, head and shoulders visible, shot on 85mm lens"
  }
};

// Ganzkörper Styles - RADIKAL VEREINFACHT (nur Framing!)
const FULLBODY_STYLES = {
  street: {
    name: "Streetstyle",
    framing: "full body shot, person fully in frame, feet visible, shot on 35mm lens"
  },
  casual: {
    name: "Casual",
    framing: "full body shot, person fully in frame, feet visible, shot on 35mm lens"
  },
  fashion: {
    name: "Fashion",
    framing: "full body shot, person fully in frame, feet visible, shot on 50mm lens"
  },
  sport: {
    name: "Sport & Action",
    framing: "full body shot, person fully in frame, feet visible, shot on 35mm lens"
  },
  cinematic_full: {
    name: "Cinematic Ganzkörper",
    framing: "full body shot, person fully in frame, feet visible, shot on 35mm lens"
  },
  luxury: {
    name: "Luxury",
    framing: "full body shot, person fully in frame, feet visible, shot on 35mm lens"
  }
};

// Anti-Glanz / natürliche Haut - MIDJOURNEY-STYLE (gekürzt)
const SKIN_BLOCK = "natural matte skin, professional photography";

// Face-Details - ENTFERNT für Midjourney-Style
const FACE_BLOCK = "";  // Leer!

// Quality / Foto-Style Block - MIDJOURNEY-STYLE (stark gekürzt)
const QUALITY_BLOCK = "8k ultra detailed, cinematic lighting, high quality";

// Negative Prompt – MIDJOURNEY-STYLE (erweitert für bessere Szenen)
export const SUPER_NEGATIVE_PROMPT = [
  // Quality Issues
  "blurry", "low quality", "distorted", "deformed", "bad anatomy",
  "ugly", "amateur", "pixelated", "jpeg artifacts",
  
  // Portrait-Blocker (für Szenen!)
  "portrait", "headshot", "close-up", "face only", "cropped",
  "tight framing", "face focus", "selfie", "passport photo",
  
  // Skin Issues
  "glossy skin", "shiny oily face", "plastic look", "beauty filter",
  "unrealistic skin", "overexposed skin", "harsh highlights on face",
  
  // Anatomy Issues
  "cut off limbs", "cropped head", "extra limbs", "extra fingers",
  "deformed hands", "double face", "weird eyes", "missing limbs"
].join(", ");

/**
 * Snapshot Super Prompt Generator
 *
 * @param {Object} options
 * @param {string} options.userText       - Eingabetext vom User (DE oder EN)
 * @param {string} options.genderToken    - "mann" | "frau" | "kind"
 * @param {string} options.tuneId         - Tune-ID für LoRA
 * @param {string} [options.bodyMode]     - "portrait" | "upper" | "three_quarter" | "full" | "cinematic"
 * @param {string} [options.cinematicMood] - "golden" | "blue" | "noir" | "neon" | "storm"
 * @param {string} [options.vibe]         - "photographic" | später mehr
 * @param {boolean} [options.randomize]   - ob Kamera/Framing/Licht randomisiert werden sollen
 * @param {Object} [options.avatarCharacteristics] - Avatar-Merkmale (age, ethnicity, eye_color, body_type, height)
 */
export const buildSuperPrompt = async ({
  userText,
  genderToken,
  tuneId,
  bodyMode = "portrait",
  cinematicMood = "golden",  // Cinematic Mood
  portraitStyle = "natural",  // NEU: Portrait Sub-Style
  fullBodyStyle = "street",   // NEU: Ganzkörper Sub-Style
  vibe = "photographic",
  randomize = true,
  avatarCharacteristics = {}
}) => {
  const translatedContent = await translateToEnglish(userText || "");

  // Build avatar description from characteristics
  let avatarDescription = "";
  
  if (avatarCharacteristics) {
    const parts = [];
    
    // Age (z.B. "30er" → "in his 30s")
    if (avatarCharacteristics.age) {
      const ageMap = {
        '18+': 'young adult',
        '20er': 'in his 20s',
        '30er': 'in his 30s',
        '40-45': 'in his early 40s',
        '46-50': 'in his late 40s',
        '51-55': 'in his early 50s',
        '56-60': 'in his late 50s',
        '61-65': 'in his early 60s',
        '66-70': 'in his late 60s'
      };
      const ageDesc = ageMap[avatarCharacteristics.age] || avatarCharacteristics.age;
      // Gender-aware: "in his/her"
      const genderPronoun = (genderToken === 'woman' || genderToken === 'frau') ? 'her' : 'his';
      parts.push(ageDesc.replace('his', genderPronoun));
    }
    
    // Ethnicity
    if (avatarCharacteristics.ethnicity) {
      parts.push(avatarCharacteristics.ethnicity);
    }
    
    // Body Type
    if (avatarCharacteristics.body_type) {
      parts.push(`with ${avatarCharacteristics.body_type}`);
    }
    
    // Height
    if (avatarCharacteristics.height) {
      parts.push(avatarCharacteristics.height);
    }
    
    // Eye Color
    if (avatarCharacteristics.eye_color) {
      parts.push(avatarCharacteristics.eye_color);
    }
    
    // Hair Description (NEU: Avatar-Frisur respektieren!)
    if (avatarCharacteristics.hair_description) {
      parts.push(avatarCharacteristics.hair_description);
    }
    
    if (parts.length > 0) {
      avatarDescription = parts.join(', ');
    }
  }

  // FIX PROBLEM 1, 4, 7: Proper mood-specific settings that don't get overwritten
  let framingPrompt;
  
  switch (bodyMode) {
    case "cinematic":
      // Cinematic: Nur Framing, GPT macht den Rest!
      const moodConfig = CINEMATIC_MOODS.find(m => m.id === cinematicMood) || CINEMATIC_MOODS[0];
      framingPrompt = moodConfig.framing;
      break;
      
    case "full":
      // Ganzkörper: Nur Framing, GPT macht den Rest!
      const fullStyle = FULLBODY_STYLES[fullBodyStyle] || FULLBODY_STYLES.street;
      framingPrompt = fullStyle.framing;
      break;
      
    case "portrait":
    default:
      // Portrait: Nur Framing, GPT macht den Rest!
      const portraitStyleConfig = PORTRAIT_STYLES[portraitStyle] || PORTRAIT_STYLES.natural;
      framingPrompt = portraitStyleConfig.framing;
      break;
  }

  const vibePrompt = "clean realistic photographic style";

  const contentParts = [
    avatarDescription,  // Avatar-Beschreibung
    framingPrompt,      // NUR Framing!
    translatedContent,  // User-Text (GPT baut Szene!)
    SKIN_BLOCK,         // Minimal
    QUALITY_BLOCK       // Minimal
  ].filter(Boolean);

  // FIX PROBLEM 5: Map German gender tokens to English
  let token = genderToken || "man";
  
  // German to English mapping
  const genderMap = {
    'frau': 'woman',
    'mann': 'man',
    'kind': 'child'
  };
  
  // Apply mapping if German token
  if (genderMap[token.toLowerCase()]) {
    token = genderMap[token.toLowerCase()];
  }
  
  // Fallback to "man" if invalid
  if (!["man", "woman", "child"].includes(token)) {
    token = "man";
  }

  let finalPrompt = "";
  if (tuneId) {
    finalPrompt = `<lora:${tuneId}:1.0> `;  // Face-Match: 1.0 für maximale Konsistenz!
  }

  finalPrompt += `ohwx ${token} ${contentParts.join(", ")}`;

  return finalPrompt;
};
